import {
  generateTermsWorkorderInterface,
  TermsTypes,
} from '@/core/domain/reports/terms/GenerateTerm.types';
import { Button, Input, Select, Tooltip } from 'antd';
import { useState } from 'react';
import useGenerateTermLGPD, {
  useGenerateTerm,
} from '@/core/domain/reports/terms/GenerateTerm.hook';
import useFileLoading, { FileType, FILE_FORMAT } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useQuestReportPermanenceDeclaration } from '@/core/domain/questReport/WorkOrder/WorkOder.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useDisclosure } from '@/hooks';
import WarrantyModal from './WarrantyModal';

export default function CustomTermsGenerateButton() {
  const intl = useIntl();

  const {
    workorder: { data: workorder },
    complement,
  } = useWorkorderDetailContext();

  const [termType, setTermType] = useState<TermsTypes>('lgpd');
  const [format, setFormat] = useState<FileType>();

  const [modalWarrantyOpened, modalWarrantyAction] = useDisclosure();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const reportName: Record<TermsTypes, string> = {
    lgpd: 'Termo LGPD',
    discharge: 'Termo de quitação',
    ['permanence-declaration']: 'Declaração de permanência',
    warranty: 'Termo de garantia',
    signatureTerm: 'Declaração de assinatura da OS',
    responsability: 'Termo de responsabilidade',
    ['supplied-parts']: 'Peças fornecidas',
    ['plate-seal-cut']: 'Termo de corte de selo de placa',
  };

  const generateTermLGPD = useGenerateTermLGPD(reportName[termType]);
  const generateTerm = useGenerateTerm();
  const generatePermanenceDeclaration = useQuestReportPermanenceDeclaration();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      if (workorder && complement.data) {
        if (termType === 'lgpd') {
          const obj = {
            companyId: workorder.company.id,
            customerId: workorder.customer?.id,
            format: FILE_FORMAT[format],
          };
          const result = await generateTermLGPD.mutateAsync(obj);

          if (callback) {
            callback(result);
          }
        } else if (termType === 'permanence-declaration') {
          const result = await generatePermanenceDeclaration.mutateAsync(
            complement.data.id,
          );

          if (callback) {
            callback(result);
          }
        } else if (termType === 'warranty') {
          setFormat(format);

          modalWarrantyAction.open();
        } else {
          const obj: generateTermsWorkorderInterface = {
            term: termType,
            complementId: complement.data.id,
          };
          const result = await generateTerm.mutateAsync(obj);
          if (callback) {
            callback(result);
          }
        }
      }
    } catch {}
    setFileUnloading(format);
  };

  const termObject = (value: TermsTypes, label: string) => {
    const translatedLabel = intl.formatMessage({
      id: 'companies.customTerms.form.' + label,
    });
    return { value, label: translatedLabel };
  };

  function validateType(): string {
    return termType === 'lgpd'
      ? workorder
        ? `termo_lgpd_${workorder.customer?.name.replace(' ', '_')}.pdf`
        : 'termo_lgpd.pdf'
      : termType === 'discharge'
      ? 'termo_de_quitacao.pdf'
      : termType === 'warranty'
      ? 'termo_de_garantia.pdf'
      : termType === 'permanence-declaration'
      ? 'termo_de_permanencia.pdf'
      : termType === 'signatureTerm'
      ? 'declaracao_de_assinatura_da_os.pdf'
      : termType === 'plate-seal-cut'
      ? 'termo_de_corte_de_selo_de_placa.pdf'
      : 'termo_de_pecas_fornecidas.pdf';
  }

  const termsOptions = [
    termObject('lgpd', 'rgpdLabel'),
    termObject('warranty', 'warrantyLabel'),
    termObject('permanence-declaration', 'stayLabel'),
    termObject('discharge', 'dischargeLabel'),
    termObject('supplied-parts', 'partsSuppliedLabel'),
    termObject('responsability', 'responsabilityLabel'),
    termObject('plate-seal-cut', 'plateSealCutTerms'),
  ];

  return (
    <>
      <Input.Group
        compact
        style={{
          flexWrap: 'nowrap',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Select
          disabled={
            !hasPermissions([
              Authorities.MOVIMENTATION_WORKORDER_CUSTOM_TERMS_VIEW,
            ])
          }
          defaultValue="lgpd"
          options={termsOptions}
          onChange={(e: TermsTypes) => setTermType(e)}
          style={{ width: '100%', overflow: 'hidden' }}
        />

        <Button
          type="primary"
          icon={
            <Tooltip title="Gerar PDF">
              <DownloadOutlined />
            </Tooltip>
          }
          key="PDF"
          onClick={() => {
            handleGenerateReport('PDF', (result) =>
              report.downloadFile({
                result,
                fileName: validateType(),
              }),
            );
          }}
          loading={fileLoading.PDF}
          disabled={
            !hasPermissions([
              Authorities.MOVIMENTATION_WORKORDER_CUSTOM_TERMS_VIEW,
            ])
          }
          style={{ minWidth: '30px' }}
        />

        <Button
          type="primary"
          icon={
            <Tooltip title="Visualizar/Imprimir">
              <EyeOutlined />
            </Tooltip>
          }
          onClick={() =>
            handleGenerateReport('REPORT_VIEW', (result) =>
              report.openFile({
                result,
                fileName: validateType(),
                fileType: 'application/pdf',
              }),
            )
          }
          key="viewReport"
          loading={fileLoading.REPORT_VIEW}
          disabled={
            !hasPermissions([
              Authorities.MOVIMENTATION_WORKORDER_CUSTOM_TERMS_VIEW,
            ])
          }
          style={{ minWidth: '30px' }}
        />
      </Input.Group>

      <WarrantyModal
        format={format}
        termType={termType}
        open={modalWarrantyOpened}
        onCancel={modalWarrantyAction.toggle}
        complementId={complement?.data?.id}
      />
    </>
  );
}
